import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  IconUserPlus,
  IconMail,
  IconBrandLinkedin,
  IconUser,
  IconBuilding,
  IconLock,
  IconEye,
  IconEyeOff,
  IconLoader2,
  IconCircleCheck,
  IconAlertCircle,
  IconChevronDown,
  IconShieldCheck,
  IconTargetArrow,
  IconBolt,
} from "@tabler/icons-react";
import classes from "./registerRedesign.module.css";
import {
  checkMail,
  checkName,
  checkPassword,
  checkPasswordNum,
  checkUpperCase,
  checkLowerCase,
  checkSpecialChar,
  checkLinkedInUrlOptional,
  validateAndCleanLinkedInUrl,
} from "../../../utils/functions";
import { positionOptions } from "../../../utils/dropdownItems";
import { talentSignup } from "../../../services/talentCalls";
import { clientSignup } from "../../../services/clientCalls";
import { markAttributionClaimed } from "../../../utils/attribution";
import { trackSignUp } from "../../../utils/analyticsEvents";
import PhoneNumberInput from "../../../components/UI/PhoneNumber/phonenumber";
import { errorHandler } from "../../../utils";
import { UserContext } from "../../../context";
import { Logo } from "../../../constants";

const BrandLogo = () => (
  <a className={classes.brandLogo} href="https://www.awasource.com">
    <Logo color="#ffffff" />
  </a>
);

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterRedesign = () => {
  const navigate = useNavigate();
  const { role, setRole } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const [initialized, setInitialized] = useState(false);

  /* Lock page scroll on desktop, where this is a fixed one-screen split layout */
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 981px)");
    const applyLock = () => {
      const locked = mql.matches ? "hidden" : "";
      document.documentElement.style.overflow = locked;
      document.body.style.overflow = locked;
    };
    applyLock();
    mql.addEventListener("change", applyLock);
    return () => {
      mql.removeEventListener("change", applyLock);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (!initialized) {
      if (tabParam === "client") setRole("client");
      else if (tabParam === "talent") setRole("talent");
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, initialized]);

  const [talentData, setTalentData] = useState({
    email: "",
    linkedInUrl: "",
    phone: "",
    password: "",
    confirm_password: "",
    agreeToEmails: true,
  });
  const [clientData, setClientData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    phone: "",
    position: "",
    password: "",
    confirm_password: "",
    agreeToEmails: true,
  });

  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [load, setLoad] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeKey, setShakeKey] = useState(null);

  const shake = (key) => {
    setShakeKey(key);
    setTimeout(() => setShakeKey((k) => (k === key ? null : k)), 400);
  };

  const setErr = (key, on) => setErrors((p) => ({ ...p, [key]: on }));
  const clearErr = (key) => setErrors((p) => ({ ...p, [key]: false }));

  const passwordChecks = (val) => ({
    length: val.length >= 10,
    lower: checkLowerCase(val),
    number: checkPasswordNum(val),
    symbol: checkUpperCase(val) || checkSpecialChar(val),
  });

  const setTalentField = (field, value) => {
    setTalentData((p) => ({ ...p, [field]: value }));
    clearErr(field);
  };
  const setClientField = (field, value) => {
    setClientData((p) => ({ ...p, [field]: value }));
    clearErr(field);
  };

  const talentSubmit = async (e) => {
    e.preventDefault();
    let ok = true;

    if (!checkMail(talentData.email.trim())) {
      setErr("email", true);
      shake("email");
      ok = false;
    }
    if (!talentData.phone || !isPhoneValid) {
      setErr("phone", true);
      shake("phone");
      ok = false;
    }
    if (!checkPassword(talentData.password)) {
      setErr("password", true);
      shake("password");
      ok = false;
    }
    if (!talentData.confirm_password || talentData.confirm_password !== talentData.password) {
      setErr("confirm_password", true);
      shake("confirm_password");
      ok = false;
    }
    if (!ok) return;

    try {
      setLoad(true);
      const cleanedLinkedInUrl = validateAndCleanLinkedInUrl(talentData.linkedInUrl);
      const payload = { ...talentData, linkedInUrl: cleanedLinkedInUrl || "" };

      await talentSignup(payload);
      markAttributionClaimed();
      trackSignUp({ method: "email", userType: "talent", email: talentData.email });
      localStorage.setItem("email", talentData.email);
      navigate("/verify-mail?status=talent");
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    }
  };

  const clientSubmit = async (e) => {
    e.preventDefault();
    let ok = true;

    if (!checkName(clientData.firstName)) {
      setErr("firstName", true);
      shake("firstName");
      ok = false;
    }
    if (!checkName(clientData.lastName)) {
      setErr("lastName", true);
      shake("lastName");
      ok = false;
    }
    if (!clientData.companyName.trim()) {
      setErr("companyName", true);
      shake("companyName");
      ok = false;
    }
    if (!checkMail(clientData.email.trim())) {
      setErr("email", true);
      shake("email");
      ok = false;
    }
    if (!clientData.phone || clientData.phone.replace(/\D/g, "").length < 7) {
      setErr("phone", true);
      shake("phone");
      ok = false;
    }
    if (!clientData.position) {
      setErr("position", true);
      shake("position");
      ok = false;
    }
    if (!checkPassword(clientData.password)) {
      setErr("password", true);
      shake("password");
      ok = false;
    }
    if (!clientData.confirm_password || clientData.confirm_password !== clientData.password) {
      setErr("confirm_password", true);
      shake("confirm_password");
      ok = false;
    }
    if (!ok) return;

    try {
      setLoad(true);
      await clientSignup(clientData);
      markAttributionClaimed();
      trackSignUp({ method: "email", userType: "client", email: clientData.email });
      localStorage.setItem("email", clientData.email);
      navigate("/verify-mail?status=client");
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Please check your internet connection and try again.", "Error");
    }
  };

  const isTalent = role === "talent";
  const talentPw = passwordChecks(talentData.password);
  const clientPw = passwordChecks(clientData.password);
  const pw = isTalent ? talentPw : clientPw;

  const handleNameChange = (setter, field) => (e) => {
    setter(field, e.target.value.replace(/[\s\d]+/g, ""));
  };

  return (
    <div className={classes.authShell}>
      <div className={classes.pageBg}>
        <div className={`${classes.brandBlob} ${classes.b1}`} />
        <div className={`${classes.brandBlob} ${classes.b2}`} />
        <div className={`${classes.brandBlob} ${classes.b3}`} />
      </div>

      <aside className={classes.brandPanel}>
        <div className={classes.brandTop}>
          <BrandLogo />
          <span className={classes.brandPanelTag}>Africa's hiring layer</span>
        </div>

        <div className={classes.brandMiddle}>
          <div className={`${classes.roleCopy} ${!isTalent ? classes.roleCopyVisible : ""}`}>
            <h1 className={classes.brandHeadline}>
              Verified talent.
              <br />
              Zero guesswork.
            </h1>
            <p className={classes.brandSub}>
              Every profile you see has been identity-checked and skills-assessed before it reaches your shortlist. Hire on
              signal, not on hope.
            </p>
          </div>
          <div className={`${classes.roleCopy} ${isTalent ? classes.roleCopyVisible : ""}`}>
            <h1 className={classes.brandHeadline}>
              Get seen.
              <br />
              Get matched.
            </h1>
            <p className={classes.brandSub}>
              Build one honest profile, let our AI surface what makes you strong, and get matched to roles that actually fit —
              no gatekeepers.
            </p>
          </div>

          <div className={classes.brandVisual}>
            <div className={`${classes.floatCard} ${isTalent ? classes.talentA : classes.clientA}`}>
              <span className={classes.fcIcon} style={{ background: "#ecfdf5", color: "#059669" }}>
                <IconShieldCheck size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Identity verified</div>
                <div className={classes.fcSub}>Checked in minutes</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${isTalent ? classes.talentB : classes.clientB}`}>
              <span className={classes.fcIcon} style={{ background: "#eff2ff", color: "#1d4ed8" }}>
                <IconTargetArrow size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>96% role fit</div>
                <div className={classes.fcSub}>AI match score</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${isTalent ? classes.talentC : classes.clientC}`}>
              <span className={classes.fcIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
                <IconBolt size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>2.4 day avg.</div>
                <div className={classes.fcSub}>Time to shortlist</div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.brandBottom}>
          <div>
            <div className={classes.brandStatValue}>10k+</div>
            <div className={classes.brandStatLabel}>Verified profiles</div>
          </div>
          <div>
            <div className={classes.brandStatValue}>4.8/5</div>
            <div className={classes.brandStatLabel}>Avg. rating</div>
          </div>
          <div>
            <div className={classes.brandStatValue}>14</div>
            <div className={classes.brandStatLabel}>Countries</div>
          </div>
        </div>
      </aside>

      <div className={classes.mobileHeaderBar}>
        <div className={classes.mobileHeaderBarInner}>
          <BrandLogo />
        </div>
      </div>

      <main className={classes.formPanel}>
        <div className={classes.formCard}>
          <div className={classes.formPanelInner}>
            <div className={`${classes.formHeader} ${classes.animIn}`}>
              <span className={classes.formEyebrow}>
                <IconUserPlus size={13} /> Create account
              </span>
              <h1 className={classes.formTitle}>Let's get you set up.</h1>
              <p className={classes.formSubtitle}>Free to join — takes less than two minutes.</p>
            </div>

            <p className={`${classes.formTopLink} ${classes.animIn}`}>
              Have an Account? <Link to="/login">Sign In</Link>
            </p>

            <div className={`${classes.roleToggle} ${classes.animIn}`}>
              <div className={`${classes.roleToggleHighlight} ${!isTalent ? classes.roleToggleHighlightClient : ""}`} />
              <button
                type="button"
                className={`${classes.roleToggleBtn} ${isTalent ? classes.roleToggleBtnActive : ""}`}
                onClick={() => {
                  setRole("talent");
                  navigate("/register?tab=talent");
                }}
              >
                Talent
              </button>
              <button
                type="button"
                className={`${classes.roleToggleBtn} ${!isTalent ? classes.roleToggleBtnActive : ""}`}
                onClick={() => {
                  setRole("client");
                  navigate("/register?tab=client");
                }}
              >
                Client
              </button>
            </div>

            <form className={classes.animIn} onSubmit={isTalent ? talentSubmit : clientSubmit} noValidate>
              {isTalent ? (
                <div className={classes.fieldGroup}>
                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="signupEmail">
                      Email Address*
                    </label>
                    <div className={classes.inputWrap}>
                      <IconMail size={16} className={classes.inputIcon} />
                      <input
                        id="signupEmail"
                        className={`${classes.formInput} ${errors.email ? classes.formInputErr : ""} ${shakeKey === "email" ? classes.shake : ""}`}
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        value={talentData.email}
                        onChange={(e) => setTalentField("email", e.target.value)}
                      />
                    </div>
                    <div className={`${classes.fieldError} ${errors.email ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Enter a valid email address.
                    </div>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="linkedinUrl">
                      LinkedIn URL
                    </label>
                    <div className={classes.inputWrap}>
                      <IconBrandLinkedin size={16} className={classes.inputIcon} />
                      <input
                        id="linkedinUrl"
                        className={classes.formInput}
                        type="text"
                        placeholder="LinkedIn Url (this is optional)"
                        autoComplete="url"
                        value={talentData.linkedInUrl}
                        onChange={(e) => setTalentField("linkedInUrl", e.target.value)}
                      />
                    </div>
                    {talentData.linkedInUrl && !checkLinkedInUrlOptional(talentData.linkedInUrl) && (
                      <div className={`${classes.fieldError} ${classes.fieldErrorOn}`}>
                        <IconAlertCircle size={13} /> Invalid url.
                      </div>
                    )}
                  </div>

                  <div className={`${classes.field} ${classes.phoneField} ${errors.phone ? classes.phoneFieldErr : ""} ${shakeKey === "phone" ? classes.shake : ""}`}>
                    <PhoneNumberInput
                      label="Phone Number*"
                      value={talentData.phone}
                      onValidityChange={setIsPhoneValid}
                      onChange={(v) => setTalentField("phone", v)}
                    />
                    <div className={`${classes.fieldError} ${errors.phone ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please enter your phone number.
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.fieldGroup}>
                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="firstName">
                      First Name*
                    </label>
                    <div className={classes.inputWrap}>
                      <IconUser size={16} className={classes.inputIcon} />
                      <input
                        id="firstName"
                        className={`${classes.formInput} ${errors.firstName ? classes.formInputErr : ""} ${shakeKey === "firstName" ? classes.shake : ""}`}
                        type="text"
                        placeholder="Jane"
                        autoComplete="given-name"
                        value={clientData.firstName}
                        onChange={handleNameChange(setClientField, "firstName")}
                      />
                    </div>
                    <div className={`${classes.fieldError} ${errors.firstName ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please enter your first name.
                    </div>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="lastName">
                      Last Name*
                    </label>
                    <div className={classes.inputWrap}>
                      <IconUser size={16} className={classes.inputIcon} />
                      <input
                        id="lastName"
                        className={`${classes.formInput} ${errors.lastName ? classes.formInputErr : ""} ${shakeKey === "lastName" ? classes.shake : ""}`}
                        type="text"
                        placeholder="Doe"
                        autoComplete="family-name"
                        value={clientData.lastName}
                        onChange={handleNameChange(setClientField, "lastName")}
                      />
                    </div>
                    <div className={`${classes.fieldError} ${errors.lastName ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please enter your last name.
                    </div>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="companyName">
                      Company Name*
                    </label>
                    <div className={classes.inputWrap}>
                      <IconBuilding size={16} className={classes.inputIcon} />
                      <input
                        id="companyName"
                        className={`${classes.formInput} ${errors.companyName ? classes.formInputErr : ""} ${shakeKey === "companyName" ? classes.shake : ""}`}
                        type="text"
                        placeholder="Your company name"
                        autoComplete="organization"
                        value={clientData.companyName}
                        onChange={(e) => setClientField("companyName", e.target.value)}
                      />
                    </div>
                    <div className={`${classes.fieldError} ${errors.companyName ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please enter your company name.
                    </div>
                  </div>

                  <div className={classes.field}>
                    <label className={classes.fieldLabel} htmlFor="workEmail">
                      Work Email*
                    </label>
                    <div className={classes.inputWrap}>
                      <IconMail size={16} className={classes.inputIcon} />
                      <input
                        id="workEmail"
                        className={`${classes.formInput} ${errors.email ? classes.formInputErr : ""} ${shakeKey === "email" ? classes.shake : ""}`}
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        value={clientData.email}
                        onChange={(e) => setClientField("email", e.target.value)}
                      />
                    </div>
                    <div className={`${classes.fieldError} ${errors.email ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Enter a valid email address.
                    </div>
                  </div>

                  <div className={`${classes.field} ${classes.phoneField} ${errors.phone ? classes.phoneFieldErr : ""} ${shakeKey === "phone" ? classes.shake : ""}`}>
                    <PhoneNumberInput
                      label="Phone Number*"
                      value={clientData.phone}
                      onChange={(v) => setClientField("phone", v)}
                    />
                    <div className={`${classes.fieldError} ${errors.phone ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please enter your phone number.
                    </div>
                  </div>

                  <div className={`${classes.field} ${shakeKey === "position" ? classes.shake : ""}`}>
                    <label className={classes.fieldLabel} htmlFor="position">
                      Position*
                    </label>
                    <div className={classes.selectWrap}>
                      <select
                        id="position"
                        className={`${classes.formSelect} ${errors.position ? classes.formSelectErr : ""}`}
                        value={clientData.position}
                        onChange={(e) => setClientField("position", e.target.value)}
                      >
                        <option value="" disabled>
                          Select Position
                        </option>
                        {positionOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <IconChevronDown size={17} className={classes.selectChevron} />
                    </div>
                    <div className={`${classes.fieldError} ${errors.position ? classes.fieldErrorOn : ""}`}>
                      <IconAlertCircle size={13} /> Please select a position.
                    </div>
                  </div>
                </div>
              )}

              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="signupPassword">
                  Password*
                </label>
                <div className={classes.inputWrap}>
                  <IconLock size={16} className={classes.inputIcon} />
                  <input
                    id="signupPassword"
                    className={`${classes.formInput} ${errors.password ? classes.formInputErr : ""} ${shakeKey === "password" ? classes.shake : ""}`}
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    style={{ paddingRight: 44 }}
                    onPaste={(e) => e.preventDefault()}
                    value={isTalent ? talentData.password : clientData.password}
                    onChange={(e) =>
                      isTalent ? setTalentField("password", e.target.value) : setClientField("password", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className={classes.pwToggleBtn}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((p) => !p)}
                  >
                    {showPassword ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                  </button>
                </div>
                <div className={classes.pwRequirements}>
                  <div className={`${classes.pwReqItem} ${pw.length ? classes.pwReqItemMet : ""}`}>
                    <IconCircleCheck size={15} /> At least 10 characters
                  </div>
                  <div className={`${classes.pwReqItem} ${pw.lower ? classes.pwReqItemMet : ""}`}>
                    <IconCircleCheck size={15} /> 1 lowercase character
                  </div>
                  <div className={`${classes.pwReqItem} ${pw.number ? classes.pwReqItemMet : ""}`}>
                    <IconCircleCheck size={15} /> 1 number
                  </div>
                  <div className={`${classes.pwReqItem} ${pw.symbol ? classes.pwReqItemMet : ""}`}>
                    <IconCircleCheck size={15} /> 1 symbol or uppercase character
                  </div>
                </div>
                <div className={`${classes.fieldError} ${errors.password ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Password doesn't meet all requirements.
                </div>
              </div>

              <div className={classes.field}>
                <label className={classes.fieldLabel} htmlFor="confirmPassword">
                  Confirm Password*
                </label>
                <div className={classes.inputWrap}>
                  <IconLock size={16} className={classes.inputIcon} />
                  <input
                    id="confirmPassword"
                    className={`${classes.formInput} ${errors.confirm_password ? classes.formInputErr : ""} ${shakeKey === "confirm_password" ? classes.shake : ""}`}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    autoComplete="new-password"
                    style={{ paddingRight: 44 }}
                    onPaste={(e) => e.preventDefault()}
                    value={isTalent ? talentData.confirm_password : clientData.confirm_password}
                    onChange={(e) =>
                      isTalent
                        ? setTalentField("confirm_password", e.target.value)
                        : setClientField("confirm_password", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className={classes.pwToggleBtn}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirmPassword((p) => !p)}
                  >
                    {showConfirmPassword ? <IconEyeOff size={17} /> : <IconEye size={17} />}
                  </button>
                </div>
                <div className={`${classes.fieldError} ${errors.confirm_password ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Passwords do not match.
                </div>
              </div>

              <div className={classes.checkRow}>
                <input
                  type="checkbox"
                  id="marketingOptIn"
                  checked={isTalent ? talentData.agreeToEmails : clientData.agreeToEmails}
                  onChange={(e) =>
                    isTalent
                      ? setTalentField("agreeToEmails", e.target.checked)
                      : setClientField("agreeToEmails", e.target.checked)
                  }
                />
                <label htmlFor="marketingOptIn">You agree to receive deals and promotional email notifications.</label>
              </div>

              <button type="submit" className={classes.btnPrimary} disabled={load}>
                {load ? (
                  <>
                    <IconLoader2 size={16} className={classes.spinIcon} /> Creating account…
                  </>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>

            <p className={classes.formFooter}>
              By proceeding you have agreed to our <Link to="/terms-of-service">Terms of Use</Link> &amp;{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterRedesign;
